var __assign = (this && this.__assign) || function () {
    __assign = Object.assign || function(t) {
        for (var s, i = 1, n = arguments.length; i < n; i++) {
            s = arguments[i];
            for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p))
                t[p] = s[p];
        }
        return t;
    };
    return __assign.apply(this, arguments);
};
import { jsx as _jsx, Fragment as _Fragment, jsxs as _jsxs } from "react/jsx-runtime";
import { useRef, useContext, useMemo } from 'react';
import { GoogleMap, Marker, Polygon, Polyline, OverlayViewF } from '@react-google-maps/api';
import { routingColors } from 'constants/mockup';
import { EMapType } from 'interface/map';
import { AppContext } from 'pages/MapViewContainer/MapView';
import { DefaultAvatar } from 'icons';
import VehicleAvatar from 'assets/image/aptive-sp-vehicle.png';
import { decode } from '@mapbox/polyline';
import styles from './index.module.scss';
var containerStyle = {
    width: '100%',
    height: '100%',
    borderRadius: '0.5rem',
};
var Map = (function (_a) {
    var mapType = _a.mapType, mapData = _a.mapData, routeLayers = _a.routeLayers, center = _a.center, zoom = _a.zoom;
    var _b = useContext(AppContext), isGoogleMapAPILoaded = _b.isGoogleMapAPILoaded, googleMapAPILoadError = _b.googleMapAPILoadError;
    var mapRef = useRef();
    var activePolygonIndex = useRef();
    var handleLoadError = function (error) {
        console.error('Error loading Google Maps:', error);
    };
    var onLoadMap = function (map) {
        mapRef.current = map;
    };
    var onClickPolygon = function (index) {
        activePolygonIndex.current = index;
    };
    var generatePolygonOption = function (routeIndex) {
        var polygonOption = {
            fillOpacity: 0.24,
            fillColor: routingColors[routeIndex % routingColors.length],
            strokeColor: routingColors[routeIndex % routingColors.length],
            strokeWeight: 2,
        };
        return polygonOption;
    };
    var generateCircleSymbol = function (routeIndex) {
        var circleSymbol = {
            path: 'M-2.3 0a2.3 2.3 0 1 0 4.6 0a2.3 2.3 0 1 0 -4.6 0',
            strokeColor: '#000',
            fillColor: routingColors[routeIndex % routingColors.length],
            fillOpacity: 1,
            strokeWidth: 1,
            strokeOpacity: 1,
            scale: 2,
        };
        return circleSymbol;
    };
    var generateLineSymbol = function (routeIndex) {
        return {
            path: 'M 0,-0.5 0,0.5',
            strokeOpacity: 1,
            scale: 2.2,
            strokeColor: routingColors[routeIndex % routingColors.length],
        };
    };
    var generateVehicle = function () {
        return (_jsx("div", __assign({ className: "".concat(styles.vehicle, " flex items-center justify-center") }, { children: _jsx("img", { src: VehicleAvatar, alt: "VehicleAvatar" }) })));
    };
    var generateAvatar = function (routeIndex, avatar) {
        var avatarBorderStyle = {
            border: "2px solid ".concat(routingColors[routeIndex % routingColors.length]),
        };
        return (_jsx(_Fragment, { children: avatar ? (_jsx("img", { className: "".concat(styles.avatar), src: "data:image/png;base64,".concat(avatar), alt: "", style: avatarBorderStyle })) : (_jsx("div", __assign({ className: "".concat(styles.avatar, " flex items-center justify-center bg-white"), style: avatarBorderStyle }, { children: _jsx(DefaultAvatar, {}) }))) }));
    };
    var routes = useMemo(function () {
        var _a, _b;
        return (_b = (_a = mapData === null || mapData === void 0 ? void 0 : mapData.routes) === null || _a === void 0 ? void 0 : _a.map(function (route, index) {
            var _a;
            return ({
                polygon: {
                    area: route.area,
                    options: generatePolygonOption(index),
                },
                polyline: {
                    path: (route.geometry ? decode(route.geometry) : []).map(function (_a) {
                        var lat = _a[0], lng = _a[1];
                        return ({ lat: lat, lng: lng });
                    }),
                    options: {
                        strokeOpacity: 0,
                        icons: [
                            {
                                icon: generateLineSymbol(index),
                                offset: '0',
                                repeat: '6px',
                            },
                        ],
                        map: mapRef.current,
                        visible: true
                    }
                },
                homes: route.appointments.map(function (appointment) { return ({
                    title: appointment.id.toString(),
                    icon: generateCircleSymbol(index),
                    position: { lat: appointment.lat, lng: appointment.lng },
                }); }),
                vehicle: {
                    position: ((_a = route.tracking_data) === null || _a === void 0 ? void 0 : _a.vehicle_location) ? {
                        lat: route.tracking_data.vehicle_location.lat,
                        lng: route.tracking_data.vehicle_location.lng,
                    } : null,
                    children: generateVehicle()
                },
                avatar: {
                    position: route.avatar_placement ? {
                        lat: route.avatar_placement.lat,
                        lng: route.avatar_placement.lng,
                    } : null,
                    children: generateAvatar(index, route.avatar),
                    icon: "data:image/png;base64,".concat(route.avatar)
                }
            });
        })) !== null && _b !== void 0 ? _b : [];
    }, [mapData]);
    var mapTypeId = useMemo(function () {
        if (mapType === EMapType.SATELLITE) {
            return 'satellite';
        }
        if (mapType === EMapType.TERRAIN) {
            return 'terrain';
        }
        if (mapType === EMapType.TRAFFIC) {
            return 'roadmap';
        }
        return 'roadmap';
    }, [mapType]);
    if (googleMapAPILoadError) {
        handleLoadError(googleMapAPILoadError);
        return _jsx("div", { children: "Error loading Google Maps!" });
    }
    return (isGoogleMapAPILoaded && mapData ?
        _jsx("div", __assign({ className: 'grow relative' }, { children: routes.length > 0 ?
                _jsx(GoogleMap, __assign({ zoom: zoom, center: center, onLoad: onLoadMap, mapContainerStyle: containerStyle, mapTypeId: mapTypeId }, { children: routes.map(function (route, index) { return (_jsxs("div", { children: [_jsx(Polyline, { visible: routeLayers.includes('routes'), path: route.polyline.path, options: route.polyline.options }), _jsx(Polygon, { visible: routeLayers.includes('route-polygon'), onMouseDown: function () { return onClickPolygon(index); }, options: route.polygon.options, paths: route.polygon.area }), route.homes.map(function (home, homeIndex) { return (_jsx(Marker, { visible: routeLayers.includes('homes'), zIndex: 1, icon: home.icon, title: home.title, position: home.position }, homeIndex)); }), routeLayers.includes('service-vehicles') && route.vehicle.position && (_jsx(OverlayViewF, { zIndex: 1, position: route.vehicle.position, mapPaneName: 'floatPane', children: route.vehicle.children })), routeLayers.includes('service-pro') && route.avatar.position && (_jsx(OverlayViewF, __assign({ zIndex: 2, position: route.avatar.position, mapPaneName: "markerLayer" }, { children: route.avatar.children })))] }, index)); }) }))
                :
                    _jsxs(_Fragment, { children: [_jsx("div", __assign({ className: 'absolate text-center hidden' }, { children: "There is no Polygon data..." })), _jsx(GoogleMap, { zoom: zoom, center: center, mapContainerStyle: containerStyle })] }) }))
        : _jsx("div", { children: "Loading Google Maps..." }));
});
export default Map;
